import {action, Action, computed, Computed, thunk, Thunk} from 'easy-peasy';
import {PeriodicCheckPayload, PeriodicCheck, PeriodicChecksPayload, Machine, LastPeriodicCheck, Project, PeriodicCheckDraftResponse, PeriodicCheckSaveResponse} from '~/types';
import {StoreModel} from "~/store";
import { generateGUID } from '~/app/helpers/guid';
import { buildChecksForSave, parsePeriodicCheckImages, resolvePeriodicCheckDraft } from '~/periodicCheck/helpers';
import { mergeCheckPayload, PeriodicCheckSessionMode } from '~/periodicCheck/offlinePeriodicCheck';
import { getQueuedPeriodicCheck } from '~/data/sync/periodicCheckQueue';
import { periodicCheckRepository } from '~/data/repositories';
export type PeriodicCheckEquipment = Pick<Machine, 'id' | 'intern' | 'name'>

interface ChecksDataPayload {
    inspectionType: Machine['inspectionType']
    checks?: PeriodicCheck[]
    draftChecks?: PeriodicCheckPayload[]
    dailyCheckId?: string | null
    lastRegisteredCheck: LastPeriodicCheck | undefined
    equipment?: PeriodicCheckEquipment
    projectId?: Project['id']
    sessionMode?: PeriodicCheckSessionMode
}

export interface OpenPeriodicCheckPayload {
    equipment: Machine
    projectId: Project['id']
    inspectionType?: Machine['inspectionType']
    lastRegisteredCheck?: LastPeriodicCheck
}

export enum InspectionTypes {
    DAILY = 0,
    PERIODIC = 1,
    ACCEPTANCE_INSPECTION = 2
}

const buildSavePayload = (
    state: {
        dailyCheckId: string | null
        checkEquipment: PeriodicCheckEquipment | null
        inspectionType: Machine['inspectionType']
        checksData: PeriodicCheckPayload[]
        projectId: Project['id'] | null
    },
    payload: PeriodicChecksPayload,
    machineId: Machine['id'],
): PeriodicChecksPayload => {
    const dailyCheckId = state.dailyCheckId ?? null;
    return {
        ...payload,
        machineId: payload.machineId ?? machineId,
        projectId: payload.projectId ?? state.projectId!,
        inspectionType: payload.inspectionType ?? state.inspectionType,
        checks: payload.checks ?? state.checksData,
        guid: payload.guid ?? generateGUID(),
        isDraft: payload.isDraft ?? 0,
        ...(dailyCheckId && { id: dailyCheckId }),
    };
};

export interface PeriodicCheckModel {
    checksData: PeriodicCheckPayload[]
    inspectionType: Machine['inspectionType']
    lastRegisteredCheck: ChecksDataPayload['lastRegisteredCheck']
    checkEquipment: PeriodicCheckEquipment | null
    projectId: Project['id'] | null
    dailyCheckId: string | null
    sessionMode: PeriodicCheckSessionMode
    setDailyCheckId: Action<PeriodicCheckModel, string | null>
    setSessionMode: Action<PeriodicCheckModel, PeriodicCheckSessionMode>
    clearDraft: Action<PeriodicCheckModel>

    currentCheck: PeriodicCheckPayload | null
    updateCurrentCheck: Action<PeriodicCheckModel, PeriodicCheckPayload | null>

    loadChecksData: Action<PeriodicCheckModel, ChecksDataPayload>
    applyCheckData: Action<PeriodicCheckModel, PeriodicCheckDraftResponse>
    updateCheckData: Action<PeriodicCheckModel, PeriodicCheckPayload>
    isChecksDataUpdated: Computed<PeriodicCheckModel>

    updating: boolean
    setUpdating: Action<PeriodicCheckModel, boolean>
    update: Thunk<PeriodicCheckModel, PeriodicChecksPayload, any, StoreModel, Promise<PeriodicCheckSaveResponse | void>>
    openFromMachine: Thunk<PeriodicCheckModel, OpenPeriodicCheckPayload, any, StoreModel, Promise<void>>
}

const withParsedImages = (check: PeriodicCheck | PeriodicCheckPayload): PeriodicCheckPayload => {
    const payload = check as PeriodicCheckPayload;
    const parsed = parsePeriodicCheckImages(payload.images, payload.im);
    return { ...payload, images: parsed.images, im: parsed.im };
};

export const periodicCheckModel: PeriodicCheckModel = {
    checksData: [],
    inspectionType: 0,
    lastRegisteredCheck: undefined,
    checkEquipment: null,
    projectId: null,
    dailyCheckId: null,
    sessionMode: 'editable',
    setDailyCheckId: action((state, id) => {
        state.dailyCheckId = id;
    }),
    setSessionMode: action((state, mode) => {
        state.sessionMode = mode;
    }),
    clearDraft: action((state) => {
        state.dailyCheckId = null;
    }),

    currentCheck: null,
    updateCurrentCheck: action((state, check) => {
        state.currentCheck = check;
    }),

    loadChecksData: action((state, data) => {
        const t = Number(data.inspectionType);
        state.inspectionType =
            t === InspectionTypes.DAILY ||
            t === InspectionTypes.PERIODIC ||
            t === InspectionTypes.ACCEPTANCE_INSPECTION
                ? t
                : InspectionTypes.DAILY;
        state.lastRegisteredCheck = data.lastRegisteredCheck;
        state.checkEquipment = data.equipment ?? null;
        state.dailyCheckId = data.dailyCheckId ?? null;
        state.sessionMode = data.sessionMode ?? 'editable';
        if (data.projectId != null) {
            state.projectId = data.projectId;
        }
        state.checksData = [];
        const draftById = new Map(
            (data.draftChecks ?? []).map(check => [String(check.id), withParsedImages(check)]),
        );
        const templates = data.checks ?? [];
        if (!templates.length && draftById.size) {
            state.checksData = Array.from(draftById.values());
            return;
        }
        templates.forEach(check => {
            state.checksData.push(mergeCheckPayload(withParsedImages(check), draftById.get(String(check.id))));
        });
    }),
    applyCheckData: action((state, checkData) => {
        if (checkData?.id != null && checkData.id !== '') {
            state.dailyCheckId = String(checkData.id);
        }
        if (!checkData?.checks?.length) {
            return;
        }
        const draftById = new Map(
            checkData.checks.map(check => [String(check.id), check])
        );
        state.checksData = state.checksData.map(item => {
            const draft = draftById.get(String(item.id));
            return draft ? mergeCheckPayload(item, withParsedImages(draft)) : item;
        });
    }),
    updateCheckData: action((state, check) => {
        state.checksData = state.checksData.map(item =>
            String(item.id) === String(check.id) ? check : item
        );
    }),
    isChecksDataUpdated: computed(state =>
        state.checksData
            .filter(check => check.isRequired)
            .every(check => check.status === 1 || check.status === 2)
    ),

    updating: false,
    setUpdating: action((state, payload) => {
        state.updating = payload;
    }),
    update: thunk(async (actions, payload, { getState, getStoreState }) => {
        const state = getState();
        const machineId = getStoreState().machine.data.id;
        const savePayload = buildSavePayload(state, payload, machineId);
        const serializedPayload = {
            ...savePayload,
            checks: buildChecksForSave(savePayload.checks),
        };
        actions.setUpdating(true);
        try {
            const response = await periodicCheckRepository.submit(serializedPayload);
            if (response?.queued) {
                actions.setSessionMode(savePayload.isDraft ? 'editable_draft' : 'submitted_pending');
                return response;
            }
            if (response?.checkData) {
                actions.applyCheckData(response.checkData);
            }
            if (savePayload.isDraft) {
                const draftId = response?.checkData?.id ?? response?.id ?? savePayload.id;
                if (draftId != null && draftId !== '') {
                    actions.setDailyCheckId(String(draftId));
                }
                actions.setSessionMode('editable_draft');
            }
            if (!savePayload.isDraft) {
                actions.clearDraft();
                actions.setSessionMode('editable');
            }
            return response;
        } finally {
            actions.setUpdating(false);
        }
    }),
    openFromMachine: thunk(async (actions, payload, { getStoreState }) => {
        const inspectionType = payload.inspectionType ?? payload.equipment.inspectionType;
        const userId = getStoreState().user.data?.id;
        const queued = userId
            ? await getQueuedPeriodicCheck(Number(userId), payload.equipment.id, Number(inspectionType))
            : null;
        const lastRegisteredCheck = payload.lastRegisteredCheck
            ?? (Number(inspectionType) === InspectionTypes.ACCEPTANCE_INSPECTION
                ? payload.equipment.lastAcceptanceInspection
                : payload.equipment.lastDailyCheck);
        const resolved = resolvePeriodicCheckDraft(
            payload.equipment,
            lastRegisteredCheck,
            inspectionType,
            queued,
        );
        const templates = Number(inspectionType) === InspectionTypes.ACCEPTANCE_INSPECTION
            ? payload.equipment.acceptanceInspectionChecks
            : payload.equipment.checks;
        actions.loadChecksData({
            checks: templates,
            inspectionType,
            lastRegisteredCheck,
            projectId: payload.projectId,
            equipment: {
                id: payload.equipment.id,
                intern: payload.equipment.intern,
                name: payload.equipment.name,
            },
            ...(resolved.draftChecks?.length ? { draftChecks: resolved.draftChecks } : {}),
            dailyCheckId: resolved.dailyCheckId,
            sessionMode: resolved.sessionMode,
        });
    }),
};
